<template>
    <div v-if="show_searches" class="legacy-ticker-dialog" @click="_closeSearchWindow">
      <div class="modal-container searches-modal-content" role="dialog" aria-modal="true" aria-label="Búsquedas guardadas" @click.stop>
        <div class="modal-header">
            <div class="title-wrapper">
                <span class="material-icons-outlined header-icon">folder_open</span>
                <h3 class="modal-title">Búsquedas Guardadas</h3>
            </div>
            <button type="button" aria-label="Cerrar búsquedas guardadas" class="modal-close-btn" @click="_closeSearchWindow()">
                <i class="material-icons">close</i>
            </button>
        </div>

        <div class="modal-body">
            <!-- Informe List View -->
            <div v-if="showInformeList" class="informe-list-view">
                <div class="informe-list-header">
                    <h3 class="informe-list-title">Seleccionar Informe</h3>
                    <p class="informe-list-subtitle">Elige un informe para cargar sus valores recomendados</p>
                </div>
                
                <div v-if="informesLoading" class="informe-list-loading">
                    <div class="loading-spinner"></div>
                    <p>Cargando informes...</p>
                </div>
                
                <div v-else-if="informes.length === 0" class="informe-list-empty">
                    <span class="material-icons-outlined">description</span>
                    <p>No hay informes disponibles</p>
                </div>
                
                <div v-else class="informe-list-container">
                    <div 
                        v-for="informe in informes.slice(0, 10)" 
                        :key="informe.nid"
                        class="informe-list-item"
                        @click="_selectInforme(informe)"
                    >
                        <div class="informe-item-content">
                            <span class="material-icons-outlined informe-icon">description</span>
                            <div class="informe-details">
                                <span class="informe-title">{{ informe.title }}</span>
                                <span class="informe-status" v-if="informe.status === 0">(No publicado)</span>
                            </div>
                            <span class="material-icons-outlined chevron">chevron_right</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation View -->
            <div v-else-if="isDeleting" class="delete-confirm-view">
                <p class="delete-question-text">¿Está seguro que desea eliminar esta búsqueda?</p>
                <div class="workspace-to-delete-info">
                    <span class="material-icons-outlined">folder</span>
                    <span class="workspace-name-delete">{{decode_finder_name(deleting_search.name)}}</span>
                </div>
                <p class="delete-warning">
                  <i class="material-icons warning-icon">warning</i>
                  Esta acción no se puede deshacer.
                </p>
            </div>

            <!-- Default Content: Searches List -->
            <div v-else>
                <div v-if="_g_UserFeatures.permissions.default_searches" class="default-searches-section">
                    <p class="section-title">Búsquedas predeterminadas</p>
                    <div class="list-container">
                        <div class="list-item clickable-item" v-for="(item) in principal_searches" :key="item.name"
                             @click="_handlePrincipalSearchClick(item)"
                             :class="{ 'disabled-item': item.texto === 'informe-search' && !isUserWithActiveSubscription }"
                             :title="item.texto === 'informe-search' && !isUserWithActiveSubscription ? 'Requiere suscripción Silver o superior' : ''">
                            <span class="item-text link-text">{{item.name}}</span>
                            <span class="material-icons-outlined item-icon-right">chevron_right</span>
                        </div>
                    </div>
                </div>

                <hr v-if="_g_UserFeatures.permissions.default_searches && personal_searches.length > 0"/>

                <div class="personal-searches-section">
                    <p class="section-title">Mis Búsquedas</p>
                    <div class="list-container" v-if="personal_searches.length > 0">
                        <div class="list-item clickable-item" v-for="(item, index) in personal_searches" :key="item.name">
                            <div v-if="finder_name != item.name" class="item-content-wrapper">
                                <span class="item-text personal-search-name" @click="_changeFinder(item.name)">{{decode_finder_name(item.name)}}</span>
                                <div class="item-actions">
                                    <button @click.stop="_prepareToEdit(item.name)" :aria-label="'Editar ' + decode_finder_name(item.name)" class="icon-button edit-icon">
                                        <i class="material-icons">edit</i>
                                    </button>
                                    <button @click.stop="_prepareToRemove(index)" :aria-label="'Eliminar ' + decode_finder_name(item.name)" class="icon-button delete-icon">
                                        <i class="material-icons">delete</i>
                                    </button>
                                </div>
                            </div>
                            <div v-else class="edit-input-wrapper" @keyup.enter="_confirmEditSearchName()" @keyup.esc="_cancelOngoingOperations()">
                                <input 
                                    type="text" 
                                    class="vanilla-input edit-search-input" 
                                    v-model="new_name" 
                                    placeholder="Nuevo nombre" 
                                    :maxlength="32"
                                    ref="editInput" 
                                    @input="_validateInputCharacters"
                                />
                                <button aria-label="Confirmar nombre" class="icon-button confirm-edit-btn" @click.stop="_confirmEditSearchName()">
                                    <i class="material-icons">check</i>
                                </button>
                                <button aria-label="Cancelar edición" class="icon-button cancel-edit-btn" @click.stop="_cancelOngoingOperations()">
                                    <i class="material-icons">close</i>
                                </button>
                            </div>
                        </div>
                    </div>
                     <div v-else class="no-items-message">
                        No hay búsquedas personales guardadas.
                    </div>
                </div>
            </div>
        </div>

        <div class="modal-footer" v-if="isDeleting">
            <button class="action-btn secondary-btn" @click="_cancelOngoingOperations()">Cancelar</button>
            <button class="action-btn delete-btn" @click="_removeSearch(deleting_search)">Eliminar</button>
        </div>
        
        <div class="modal-footer" v-if="showInformeList">
            <button class="action-btn secondary-btn" @click="_hideInformeList()">Volver</button>
        </div>
      </div>
    </div>
</template>

<script>
import { navigateLegacyQuery } from "@/features/stockchart/services/legacyQueryActivation";
import { activateSavedQuery } from "@/features/stockchart/services/legacyQueryActivation";
import { legacyQueryConsumer } from "@/features/stockchart/models/legacyQuerySelection";
    import APICaller from "@/shared/mixins/APICaller.js";
    import GetStocks from "@/shared/mixins/GetStocks.js";

    import { mapMutations, mapGetters } from "vuex";
    export default {
        mixins: [legacyQueryConsumer, APICaller, GetStocks],
        name: "Searches",
        components:{
        },
        props: {
            show_searches: {type: Boolean},
        },
        data: () =>({
            principal_searches: [],
            personal_searches: [],
            last_ipos: [],
            show_button: true,
            isClosingShieldOn: false,
            isSaving: false,
            isDeleting: false,
            deleting_search: {},
            saved_search: "",
            isEditing: false,
            finder_name: "",
            new_name: "",
            // Informe list functionality
            showInformeList: false,
            informes: [],
            informesLoading: false,
        }),
        created(){

        },
        computed: { 
            ...mapGetters(['_g_open_searches']),  
            ...mapGetters(['_g_toggle_chart_stocks']),
            ...mapGetters(['_g_UserFeatures']),
            isUserWithActiveSubscription() {
                return !!(this._g_UserFeatures?.is_platinum_user || this._g_UserFeatures?.is_gold_user || this._g_UserFeatures?.is_silver_user);
            },
        },
        mounted(){
            this._listPrincipalSearches();
            this._listPersonalSearches();
            //this._getStocksIPOs();
        },
        methods: {
            ...mapMutations(['setMenuSelectionOut']),
            ...mapMutations(['setStockValue']),
            ...mapMutations(['setOptionMobile']),
            ...mapMutations(['setOpenSearches']),
            ...mapMutations(['setChartView']),
            ...mapMutations(['setStocksView']),
            

            _listPrincipalSearches: function() {
                let search1 = {name:"Ultimas IPOs", texto:"last-ipos"};
                let search2 = {name:"Mejores alcistas última semana", texto:"best-bullish-values/last_week"};
                let search3 = {name:"Mejores alcistas último mes", texto:"best-bullish-values/last_month"};
                let search4 = {name:"Mas comentados", texto:"hot-values/last_week"};
                let search5 = {name:"Búsqueda por Informe", texto:"informe-search"};

                this.principal_searches.push(search1,search2,search3,search4,search5);
            },
            _handlePrincipalSearchClick: function(item) {
                if (item.texto === 'informe-search') {
                    if (!this.isUserWithActiveSubscription) {
                        this._notifyPremiumRequired();
                        return;
                    }
                    this._showInformeList();
                } else {
                    this._goToGD(item.texto);
                }
            },
            _goToGD: function (url) {
                if (url === 'informe-search') {
                    if (!this.isUserWithActiveSubscription) {
                        this._notifyPremiumRequired();
                        return;
                    }
                    this._showInformeList();
                } else {
                    navigateLegacyQuery(this, '/app/graficador-dinamico/' + url);
                    this._closeSearchWindow();
                }
            },
            _listPersonalSearches: function() {
                let success = (response) => {              
                    this.personal_searches = [];
                    for(let i=0; i < response.data.length; i++){
                        if(response.data[i] != "last_search"){
                            let search1 = {name:response.data[i], texto:""};
                            this.personal_searches.push(search1); 
                        }                         
                    }
                };

                let url = "/api/v1/stock-finder/finder-names";

                const successHandler = new APICaller.SuccessHandler(success);
                const failureHandler = new APICaller.FailureHandler('070', 'Error cargando búsquedas.');

                this._getAPICall(url, successHandler, failureHandler);
            },
            decode_finder_name: function(encoded_name) {
                return encoded_name.replace(/Q1SPACEQ1/g, ' ');
            },
            _closeSearchWindow: function(){
                this._cancelOngoingOperations();
                this.$emit("_closeSearchWindow");
            },

            _getStocksIPOs: function(){
                let success = response =>{
                    this.last_ipos = response.data;
                    this.$emit("last_ipos", this.last_ipos);
                }

                let url = '/api/v1/stock-finder/last-ipos'

                const successHandler = new APICaller.SuccessHandler(success);
                const failureHandler = new APICaller.FailureHandler('071', 'Error recuperando valores.');

                this._getAPICall(url, successHandler, failureHandler);
            },


            _prepareToRemove: function(index){
                this.isDeleting = !this.isDeleting;
                this.deleting_search = this.personal_searches[index];
            },
            _removeSearch: function(value) {         
                let success = () => {
                    this.$vueOnToast.pop("success", "Búsqueda", "eliminada con éxito.");
                    this._listPersonalSearches();
                    this._cancelOngoingOperations();
                               
                };
                let failure = () => {};

                let url = "/api/v1/stock-finder/delete-finder/" + value.name;

                const successHandler = new APICaller.SuccessHandler(success);
                const failureHandler = new APICaller.FailureHandler('072', 'Error eliminando búsqueda.', failure);

                this._getAPICall(url, successHandler, failureHandler);
            },
            _showInput: function(){
                this.show_button = !this.show_button;
                this.isSaving = !this.isSaving;
            },
             _manageExitClicksBehavior: function() {
                console.warn("_manageExitClicksBehavior might be redundant with modal backdrop");
            },
            _turnOnClosingShield: function() {
                this.isClosingShieldOn = true;
            },
            _cancelOngoingOperations: function(){
                this.isDeleting = false;
                this.isEditing = false;
                this.finder_name = "";
                this.new_name = "";
                this.deleting_search = {};
                this.showInformeList = false;
            },
            _saveSearch(value) {                
                var search = value[0];                
                this.saved_search = search;                        
                
            },
            ...mapGetters(['_g_FinderFilters']),
            _saveSearchToApi: function(){                
                var params = new URLSearchParams();
                params.append('name', this.saved_search);
                params.append('finder', JSON.stringify(this._g_FinderFilters()));

                let success = () => {
                    this.$vueOnToast.pop("success", "Búsqueda", "Guardada con éxito!");
                    this._listPersonalSearches();
                    this._cancelOngoingOperations();
                };

                let url = "/api/v1/stock-finder/create-finder";
                
                const successHandler = new APICaller.SuccessHandler(success);
                const failureHandler = new APICaller.FailureHandler('272', 'Error guardando la búsqueda.');

                this._postAPICall(url, params, successHandler, failureHandler);
            },
            
            _changeFinder: function(value){
                const query = this.legacyQuery, token = query && query.capture();
                let success = async () => {
                    if (this._isDestroyed || (query && !query.isCurrent(token))) return;
                    if (!await activateSavedQuery(this)) return;
                    this._closeSearchWindow();
                    this.$vueOnToast.pop("success", "Búsqueda", "Cargada con éxito!");
                };

                let url = "/api/v1/stock-finder/change-finder/" + value;

                const successHandler = new APICaller.SuccessHandler(success);
                const failureHandler = new APICaller.FailureHandler('073', 'Error cargando búsqueda.');

                this._getAPICall(url, successHandler, failureHandler);
            },

            _isValidChar: function(searchText) {
                return /^[A-Z]+$/i.test(searchText) || /^[a-zA-Z\u00C0-\u017F]+$/.test(searchText) || /\d/.test(searchText);
            },
            _hasWhiteSpace: function(s) {
                return /\s/g.test(s);
            },

            _validateInputCharacters: function(event) {
                // Only allow letters, whitespaces, digits, hyphens, and slashes
                const validCharRegex = /^[a-zA-ZÀ-ÿ0-9\s\-/]*$/;
                if (!validCharRegex.test(this.new_name)) {
                    // Remove invalid characters
                    this.new_name = this.new_name.replace(/[^a-zA-ZÀ-ÿ0-9\s\-/]/g, '');
                }
            },

            _prepareToEdit: function(name){
                this.new_name = this.decode_finder_name(name);
                this.isEditing = true;
                this.finder_name = name;
                // Focus the input after the DOM updates
                this.$nextTick(() => {
                    const inputElement = this.$refs.editInput;
                    if (inputElement && typeof inputElement.focus === 'function') {
                        inputElement.focus();
                    }
                });
            },

            _confirmEditSearchName: function(){
                const trimmedNewName = this.new_name.trim();
                if(trimmedNewName === ""){
                    this.$vueOnToast.pop('error', "Error", "El nombre no puede estar en blanco.");
                    return;
                }
                if(trimmedNewName === this.decode_finder_name(this.finder_name)) {
                    this.$vueOnToast.pop('info', "Info", "El nombre no ha cambiado.");
                    this._cancelOngoingOperations();
                    return;
                }

                var params = new URLSearchParams();
                params.append('name', this.finder_name);
                params.append('new_name', trimmedNewName); // Assuming trimmedNewName is defined earlier

                let success = response =>{
                    if (response.data == null) {
                        this.$vueOnToast.pop("success", "Búsqueda", "editada con éxito.");
                        this._cancelOngoingOperations();
                        this._listPersonalSearches();
                    } else {
                        this.$vueOnToast.pop('error', "Error", response.data.message || 'No se pudo editar la búsqueda.');
                        this._cancelOngoingOperations();
                    }
                }

                let failure = () => {
                    // failure handler already calls it, which is good.
                    this._cancelOngoingOperations();
                };

                let url = "/api/v1/stock-finder/update-finder"

                const successHandler = new APICaller.SuccessHandler(success);
                // Consider adding the failure callback passed here to the FailureHandler constructor if needed
                const failureHandler = new APICaller.FailureHandler('273', 'Error guardando cambios.', failure);

                this._postAPICall(url, params, successHandler, failureHandler);
            },

            // Informe list methods
            _showInformeList: function() {
                if (!this.isUserWithActiveSubscription) {
                    this._notifyPremiumRequired();
                    return;
                }
                this.showInformeList = true;
                this._fetchInformes();
            },

            _hideInformeList: function() {
                this.showInformeList = false;
            },

            _fetchInformes: function() {
                this.informesLoading = true;
                
                let success = response => {
                    this.informes = response.data;
                    this.informesLoading = false;
                };
                
                let url = `/api/informes-list`;
                
                const successHandler = new APICaller.SuccessHandler(success);
                const failureHandler = new APICaller.FailureHandler('400', 'Error al cargar la lista de informes', () => {
                    this.informesLoading = false;
                });
                
                this._getAPICall(url, successHandler, failureHandler);
            },

            _selectInforme: function(informe) {
                if (!this.isUserWithActiveSubscription) {
                    this._notifyPremiumRequired();
                    return;
                }
                const informeSlug = this._formatTitleForUrl(informe.title);
                navigateLegacyQuery(this, `/app/graficador-dinamico/informe/${informeSlug}`);
                this._closeSearchWindow();
            },

            _formatTitleForUrl: function(title) {
                // Convert title from 'dd/mm/yyyy' format to 'dd-mm-yyyy' format for URL
                if (!title) return '';
                const parts = title.split('/');
                if (parts.length === 3) {
                    // Return 'dd-mm-yyyy' format, URL encoded
                    return encodeURIComponent(`${parts[0]}-${parts[1]}-${parts[2]}`);
                }
                // Fallback for unexpected formats - encode the original title
                return encodeURIComponent(title);
            },

            _formatInformeDate: function(created) {
                const date = new Date(created);
                return date.toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            },

            _notifyPremiumRequired: function() {
                if (this.$vueOnToast && typeof this.$vueOnToast.pop === 'function') {
                    this.$vueOnToast.pop('info', 'Función Premium', 'Requiere suscripción Silver o superior.');
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
@import "../styles/legacy-ticker-dialogs.scss";
.legacy-ticker-dialog {
  .list-item { align-items: center; justify-content: space-between; border-radius: 0; }
  .item-text { flex: 1; min-width: 0; color: var(--dialog-text); }
  .personal-search-name { cursor: pointer; }
  .item-icon-right { font-size: 20px; color: var(--dialog-muted); }
  .section-title { margin-bottom: 8px; }
  .no-items-message { padding: 20px; text-align: center; color: var(--dialog-muted); font-size: 14px; }
  hr { border: 0; height: 1px; margin: 16px 0; background: var(--dialog-border); }
}

// Informe list styles
.informe-list-view {
    padding: 10px 0;
}

.informe-list-header {
    text-align: center;
    margin-bottom: 20px;
}

.informe-list-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--dialog-text);
    margin-bottom: 8px;
}

.informe-list-subtitle {
    font-size: 14px;
    color: var(--dialog-muted);
    margin: 0;
}

.informe-list-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    color: var(--dialog-muted);
    
    .loading-spinner {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid var(--dialog-border);
        border-top-color: var(--dialog-edge);
        animation: spin 1s infinite linear;
        margin-bottom: 16px;
    }
    
    p {
        margin: 0;
        font-size: 14px;
    }
}

.informe-list-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    color: var(--dialog-muted);
    
    .material-icons-outlined {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
    }
    
    p {
        margin: 0;
        font-size: 14px;
        font-style: italic;
    }
}

.informe-list-container {
    max-height: 300px;
    overflow-y: auto;
}

.informe-list-container::-webkit-scrollbar {
    width: 8px;
}

.informe-list-container::-webkit-scrollbar-track {
    background: var(--dialog-surface);
    border-radius: 4px;
}

.informe-list-container::-webkit-scrollbar-thumb {
    background-color: var(--dialog-control-border);
    border-radius: 4px;
    border: 2px solid var(--dialog-surface);
}

.informe-list-item {
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-radius: 0;
    margin-bottom: 2px;
    
    &:hover {
        background-color: var(--dialog-surface);
    }
}

.informe-item-content {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    gap: 12px;
}

.informe-icon {
    color: var(--dialog-edge);
    font-size: 20px;
    flex-shrink: 0;
}

.informe-details {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.informe-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--dialog-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.informe-status {
    font-size: 12px;
    color: var(--dialog-muted);
    font-style: italic;
}

.chevron {
    color: var(--dialog-muted);
    font-size: 20px;
    flex-shrink: 0;
}

.informe-list-item:hover .chevron {
    color: var(--dialog-edge);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Disabled state for Informe search item */
.disabled-item {
    opacity: 0.6;
    cursor: not-allowed;
}

.disabled-item:hover {
    background-color: inherit;
    color: inherit;
}
</style>
